import { Injectable } from "@angular/core";
import { GlobalService } from "app/global/global.service";
import { BasePermissionService } from "app/global/permissions/base-permission-service";
import { PermissionsEnum } from "app/global/permissions/permissions.enum";
import { PermissionsService } from "app/global/permissions/permissions.service";
import { UserSettings, Workspace } from "app/global/user-settings.dto";
import { checkCategoryOverDocType } from "app/store/selectors/roles.selectors";
import { UserSettingsFacade } from "app/store/user-settings.facade";
import { filter, forkJoin, map, Observable, shareReplay, switchMap, take, tap } from "rxjs";
import { IUserResultT } from "../contract-control/contract-control.permission.service";
import { AccessLevelEnum, AccessLevelPriority } from "../users/roles/roles.service";
import { IDocumentDetailRootDTO } from "./dto/document-details.dto";
import { DocumentStatusesEnum } from "./dto/document-statuses.enum";
import { DocumentListingFacade } from "./store/document-listing.facade";

export interface DocumentPermissions {
    // These are provided synchronously as booleans.
    canResendDocument: boolean;
    canEditSendDocument: boolean;
    canRevokeDocument: boolean;
    canDownloadDocument: boolean;
    canDeletedSignedDocument: boolean;

    // These are built from observables.
    canSoftDeleteDocument: boolean;
    canDeleteDocumentPermanently: boolean;
    canEditDocument: boolean;
    hasFullAccessToDocument: boolean
    hasViewDocumentPermission: boolean
}

@Injectable({
    providedIn: 'root'
})

export class DocumentsPermissionsService extends BasePermissionService {


    private deleteDocumentStatuses: string[] = [DocumentStatusesEnum.Completed, DocumentStatusesEnum.Signed, DocumentStatusesEnum.Uploaded];

    constructor(
        protected _permissionService: PermissionsService,
        protected _userSettingsFacade: UserSettingsFacade,
        protected _globalService: GlobalService,
        private _documentsFacade: DocumentListingFacade
    ) {
        super(
            _permissionService,
            _permissionService.isTenantOwner$,
            _globalService.getUserDefaultWorkSpace(),
            _userSettingsFacade._userSettings$
        )
    }



    /**
     * Retrieves the permissions for the current document.
     *
     * @returns {Observable<DocumentPermissions>} An observable that emits the document permissions.
     *
     * The permissions include:
     * - `canResendDocument`: Whether the user can resend the document.
     * - `canEditSendDocument`: Whether the user can edit the sent document.
     * - `canRevokeDocument`: Whether the user can revoke the document.
     * - `canDownloadDocument`: Whether the user can download the sent document.
     * - `canDeletedSignedDocument`: Whether the user can delete signed documents.
     * - `canSoftDeleteDocument`: Whether the user can soft delete the document.
     * - `canDeleteDocumentPermanently`: Whether the user can permanently delete the document.
     * - `canEditDocument`: Whether the user can edit the document.
     */
    getPermissions(): Observable<{ canResendDocument: boolean; canEditSendDocument: boolean; canRevokeDocument: boolean; canDownloadDocument: boolean; canDeletedSignedDocument: boolean; canSoftDeleteDocument: boolean; canDeleteDocumentPermanently: boolean; canEditDocument: boolean; hasFullAccessToDocument: boolean; hasViewDocumentPermission: boolean; }> {
        return this._documentsFacade.selectedDocument$.pipe(
            filter(x => x !== null),
            switchMap((document: IDocumentDetailRootDTO) =>
                {
                    const selectedCategoryIds = document?.layout_settings?.selected_category_ids ?? [];

                    return forkJoin({
                        canResendDocument: this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_copy_and_resend_sent_documents).pipe(take(1)),
                        canEditSendDocument: this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_edit_sent_document).pipe(take(1)),
                        canRevokeDocument: this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_revoke_document).pipe(take(1)),
                        canDownloadDocument: this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_download_sent_documents).pipe(take(1)),
                        canDeletedSignedDocument: this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_delete_signed_documents).pipe(take(1)),

                        canSoftDeleteDocument: this.hasFullAccess(document.template_details?.document_type_id, document.template_details?.document_type_id, selectedCategoryIds, document.template).pipe(
                            take(1),
                            map(hasAccess => this.deleteDocumentStatuses.includes(document?.template?.status as string) && hasAccess)
                        ),

                        canDeleteDocumentPermanently: this.hasFullAccess(document.template_details?.document_type_id, document.template_details?.document_type_id, selectedCategoryIds, document.template).pipe(
                            take(1),
                            map(hasAccess => !this.deleteDocumentStatuses.includes(document?.template?.status as string) && hasAccess)
                        ),

                        canEditDocument: this.canEditAccess(document.template_details?.document_type_id, document.template_details?.document_type_id, selectedCategoryIds, document.template).pipe(
                            take(1),
                        ),

                        hasFullAccessToDocument: this.hasFullAccess(document.template_details?.document_type_id, document.template_details?.document_type_id, selectedCategoryIds, document.template).pipe(
                            take(1),
                        ),

                        hasViewDocumentPermission: this.hasPermissionToView(document.template_details?.document_type_id, document.template_details?.document_type_id, selectedCategoryIds, document.template).pipe(
                            take(1),
                        )
                    });
                }
            ),
            shareReplay(1)
        );
    }



    /**
     * Check if the user has the required access level for a given resource.
     * @param id
     * @param documentTypeId
     * @param categoryId
     * @description This method checks if the user has the required access level for a given resource.
     *
     * @see IUserResultT
     * @see AccessLevelEnum
     * @see UserSettings
     * @returns AccessLevelEnum
     */
    protected getAccessLevel(id: number, documentTypeId: number, categoryId: number | number[]): Observable<AccessLevelEnum> {
        return this._permissionService.documentAccessLevel(documentTypeId, categoryId);
    }



    /**
     * Checks if the user has permission to view a document of a specific type.
     *
     * @param documentTypeId - The ID of the document type.
     * @param docTypeId - The ID of the document type.
     * @param categoryId - The ID of the category.
     * @param userResult - The user result object containing user information.
     * @returns An Observable that emits a boolean indicating whether the user has permission to view the document.
     */
    private hasPermissionToView(documentTypeId: number, docTypeId: number, categoryId: number | number[], userResult: IUserResultT): Observable<boolean> {
        return this.verifyPermission(documentTypeId, docTypeId, categoryId, userResult).pipe(
            map(x => x === AccessLevelEnum.view)
        );
    }




    /**
     * Check if the user has the required access level for a given resource.
     * 
     * @param hasRoleFullAccess
     * @param accessLevel
     * 
     * @description This method checks if the user has the required access level for a given resource.
     * If the user has full access, the access level is returned, otherwise null is returned.
     * 
     * @see AccessLevelEnum
     * @returns AccessLevelEnum 
     */
    protected checkRolesAccessLevel(hasRoleFullAccess: boolean, accessLevel: AccessLevelEnum): AccessLevelEnum {
        return hasRoleFullAccess ? AccessLevelEnum.full_access : accessLevel;
    }



    /**
     * Determines if the user has edit access to a document based on its type and the user's result.
     *
     * @param documentTypeId - The ID of the document type.
     * @param docTypeId  - The ID of the document type.
     * @param categoryId  - The ID of the category.
     * @param userResult - The result object containing user information and permissions.
     * @returns An Observable that emits a boolean indicating whether the user has edit access.
     */
    canEditAccess(documentTypeId: number, docTypeId: number, categoryId: number | number[], userResult: IUserResultT): Observable<boolean> {
        return this.verifyPermission(documentTypeId, docTypeId, categoryId, userResult).pipe(
            map(x => [AccessLevelEnum.edit, AccessLevelEnum.full_access, AccessLevelEnum.use_and_edit].includes(x))
        );
    }



    /**
     * Checks if the user has full access to a document type.
     *
     * @param documentTypeId - The ID of the document type.
     * @param docTypeId - The ID of the document type.
     * @param categoryId - The ID of the category 
     * @param userResult - The user result object containing user information.
     * @returns An Observable that emits a boolean indicating whether the user has full access.
     */
    hasFullAccess(documentTypeId: number, docTypeId: number, categoryId: number | number[], userResult: IUserResultT): Observable<boolean> {
        return this.verifyPermission(documentTypeId, docTypeId, categoryId, userResult).pipe(
            map(x => x === AccessLevelEnum.full_access)
        );
    }





    /**
     * Checks the group access level for a user based on their settings, the result of a user query, 
     * the default workspace, the document type ID, and the required access level.
     *
     * @param userSettings - The settings of the user, including their groups and ID.
     * @param result - The result of a user query, containing user and workspace information.
     * @param defaultWorkSpace - The default workspace to compare against the result's workspace ID.
     * @param documentTypeId - The ID of the document type to check access for.
     * @param requiredAccessLevel - The required access level to check against.
     * 
     * @description This method checks the group access level for a user based on their settings, the result of a user query,
     * the default workspace, the document type ID, and the required access level.
     * 
     * @see UserSettings
     * @see IUserResultT
     * @see Workspace
     * @see AccessLevelEnum
     * 
     * @returns The highest access level the user has for the specified document type, or null if no access is found.
     */
    protected checkGroupAccess(userSettings: UserSettings, result: IUserResultT, defaultWorkSpace: Workspace, id: number, documentTypeId: number, categoryIds: number | number[], parentCategoryIds: number[]): AccessLevelEnum {
        const highestAccessLevel: AccessLevelEnum | null = null;
        if (userSettings.id === result.user_id) {
            return AccessLevelEnum.full_access;
        }
        if (defaultWorkSpace.id !== result.workspace_id) {
            return highestAccessLevel;
        }
        return this.getHighestAccessLevel(userSettings, documentTypeId, categoryIds, parentCategoryIds);
    }



    /**
     * Determines the highest access level for a user based on their settings and the required access level.
     *
     * @param userSettings$ - The user settings containing group information and user ID.
     * @param documentTypeId - The ID of the template type to check access for.
     * 
     * @description This method determines the highest access level for a user based on their settings and the required access level.
     * @see UserSettings
     * @see AccessLevelEnum
     * 
     * @returns The highest access level the user has for the specified template type, or null if no access is found.
     */
    private getHighestAccessLevel(userSettings$: UserSettings, documentTypeId: number, categoryIds: number | number[], parentCategoryIds: number[]): AccessLevelEnum {

        const accessLevelPriority = AccessLevelPriority;
        const appliedAccessLevels: AccessLevelEnum[] = [];

        for (const group of userSettings$.groups) {
            if (!group.document_types?.length) continue;

            // Check if the user is in the group
            const groupUser = group.group_users.find(x => x.user_id === userSettings$.id);
            if (!groupUser) continue; // Skip if user is not in this group

            // Check if the document type and category is in the group
            const isDocumentTypesContains = group.document_types.some(x => x.document_type_id === documentTypeId && checkCategoryOverDocType(x, categoryIds, parentCategoryIds));

            // Validate access based on the required level
            if (
                [AccessLevelEnum.full_access, AccessLevelEnum.use_and_edit, AccessLevelEnum.use_only].includes(groupUser.documents_access_level) &&
                isDocumentTypesContains &&
                (groupUser)
            ) {
                appliedAccessLevels.push(groupUser.documents_access_level);
            }
        }

        // Determine the highest priority access level
        return appliedAccessLevels.sort((a, b) => accessLevelPriority.indexOf(a) - accessLevelPriority.indexOf(b))[0] || null;
    }
}
